"use client";

import { useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";

export default function HeroSection() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!heroRef.current) return;
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;
      const xPct = (clientX / innerWidth - 0.5) * 20;
      const yPct = (clientY / innerHeight - 0.5) * 10;
      const bg = heroRef.current.querySelector('.hero-bg-img') as HTMLElement;
      if (bg) {
        bg.style.transform = `scale(1.08) translate(${xPct * 0.3}px, ${yPct * 0.3}px)`;
      }
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <section
      ref={heroRef}
      className="hero-section"
      aria-label="Beecon Palliative Care — Home Nursing Kampala Uganda">
      
      {/* Background Image */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <div className="hero-bg-img w-full h-full transition-transform duration-700 ease-out" style={{ transform: 'scale(1.08)' }}>
          <AppImage
            src="/assets/images/black_nurse_bedside_assisted_living_fancy_home.png"
            alt="Black nurse providing compassionate bedside assisted living care to patient in upscale home setting"
            fill
            className="object-cover object-center"
            priority />
          
        </div>
        <div className="hero-overlay absolute inset-0 z-10" />
        {/* Gold vignette bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-64 z-10" style={{
          background: 'linear-gradient(to top, rgba(6,21,48,0.95) 0%, transparent 100%)'
        }} />
      </div>

      {/* Content */}
      <div className="relative z-20 flex flex-col min-h-screen">
        {/* Top badge */}
        <div className="flex justify-center pt-24 md:pt-32 px-4 md:px-6">
          <div className="glass-card px-4 py-2 md:px-5 md:py-2.5 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse flex-shrink-0" />
            <span className="text-[10px] md:text-xs font-medium text-cream/80 tracking-wider uppercase font-sans">
              Uganda&apos;s Premier Home Palliative Care
            </span>
          </div>
        </div>

        {/* Main headline */}
        <div className="flex-1 flex flex-col items-center justify-center px-4 md:px-6 text-center py-8 md:py-12">
          <h1 className="font-serif text-3xl sm:text-4xl md:text-6xl lg:text-8xl font-light text-white leading-[1.1] md:leading-[0.95] tracking-tight mb-4 md:mb-6 max-w-5xl">
            Compassionate Care,{" "}
            <span className="gold-shimmer italic">Delivered Home</span>
          </h1>
          <p className="text-cream/70 text-sm sm:text-base md:text-lg lg:text-xl max-w-2xl mx-auto leading-relaxed font-sans mb-6 md:mb-10 px-2">
            Professional bedside nursing, elderly care, and palliative support in Kololo, Naguru, Nakasero, and across Kampala&apos;s finest neighbourhoods.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center mb-8 md:mb-16 w-full max-w-md sm:max-w-none px-2">
            <a
              href="https://wa.me/256788217719"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold flex items-center justify-center gap-2 md:gap-3 px-6 md:px-8 py-3 md:py-4 text-sm md:text-base font-bold"
              aria-label="Book a nurse via WhatsApp">
              
              <svg viewBox="0 0 24 24" className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0" fill="currentColor" aria-hidden="true">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              <span className="whitespace-nowrap">Book a Nurse Today</span>
            </a>
            <a
              href="#contact"
              className="btn-outline-gold flex items-center justify-center gap-2 px-6 md:px-8 py-3 md:py-4 text-sm md:text-base"
              aria-label="Request a home visit">
              
              <span className="whitespace-nowrap">Request a Home Visit</span>
              <Icon name="ArrowDownIcon" size={16} className="text-gold md:w-[18px] md:h-[18px]" />
            </a>
          </div>

          {/* Floating stat cards */}
          <div className="grid grid-cols-2 sm:flex sm:flex-wrap justify-center gap-2 sm:gap-3 md:gap-4 max-w-3xl mx-auto px-2">
            <div className="glass-card-dark float-1 px-3 py-3 md:px-5 md:py-4 flex items-center gap-2 md:gap-3 min-w-[140px] sm:min-w-[160px]">
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(201,168,76,0.15)' }}>
                <Icon name="HeartIcon" size={18} className="text-gold md:w-5 md:h-5" variant="solid" />
              </div>
              <div>
                <div className="text-xl md:text-2xl font-serif font-light text-white leading-none">1:1</div>
                <div className="text-[10px] md:text-xs text-cream/60 font-sans mt-0.5">Dedicated Nurse</div>
              </div>
            </div>

            <div className="glass-card-dark float-2 px-3 py-3 md:px-5 md:py-4 flex items-center gap-2 md:gap-3 min-w-[140px] sm:min-w-[160px]">
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(201,168,76,0.15)' }}>
                <Icon name="ClockIcon" size={18} className="text-gold md:w-5 md:h-5" variant="solid" />
              </div>
              <div>
                <div className="text-xl md:text-2xl font-serif font-light text-white leading-none">24/7</div>
                <div className="text-[10px] md:text-xs text-cream/60 font-sans mt-0.5">Available Care</div>
              </div>
            </div>

            <div className="glass-card-dark float-3 px-3 py-3 md:px-5 md:py-4 flex items-center gap-2 md:gap-3 min-w-[140px] sm:min-w-[160px]">
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(201,168,76,0.15)' }}>
                <Icon name="MapPinIcon" size={18} className="text-gold md:w-5 md:h-5" variant="solid" />
              </div>
              <div>
                <div className="text-xl md:text-2xl font-serif font-light text-white leading-none">12+</div>
                <div className="text-[10px] md:text-xs text-cream/60 font-sans mt-0.5">Kampala Areas</div>
              </div>
            </div>

            <div className="glass-card-dark float-1 px-3 py-3 md:px-5 md:py-4 flex items-center gap-2 md:gap-3 min-w-[140px] sm:min-w-[160px]" style={{ animationDelay: '-3s' }}>
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(201,168,76,0.15)' }}>
                <Icon name="AcademicCapIcon" size={18} className="text-gold md:w-5 md:h-5" variant="solid" />
              </div>
              <div>
                <div className="text-xl md:text-2xl font-serif font-light text-white leading-none">RN</div>
                <div className="text-[10px] md:text-xs text-cream/60 font-sans mt-0.5">Qualified Nurses</div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="flex justify-center pb-6 md:pb-8 z-20">
          <div className="flex flex-col items-center gap-2 opacity-50">
            <span className="text-[10px] md:text-xs text-cream/60 font-sans tracking-widest uppercase">Scroll</span>
            <div className="w-px h-6 md:h-8 bg-gradient-to-b from-gold/60 to-transparent" />
          </div>
        </div>
      </div>
    </section>);

}